/* global axios */

/**
 * Evolution API Client
 * Supports both Evolution GO (Go-based) and Evolution Node (Node.js-based) APIs
 * Evolution GO uses different endpoint patterns and authentication
 */
class EvolutionAPI {
  /**
   * Logout from an instance
   * Evolution GO: DELETE /instance/logout (with instanceToken in apikey header)
   * Evolution Node: DELETE /instance/logout/{instanceName}
   */
  static async logout(apiUrl, adminToken, instanceName, instanceToken = null) {
    if (!apiUrl || !adminToken || !instanceName) {
      return Promise.reject(new Error('Missing Evolution credentials'));
    }

    const baseUrl = apiUrl.replace(/\/$/, '');

    try {
      // Evolution GO: uses instance token and no instance name in URL
      if (instanceToken) {
        const response = await axios.delete(`${baseUrl}/instance/logout`, {
          headers: {
            apikey: instanceToken,
            'Content-Type': 'application/json',
          },
        });
        return response.data;
      }

      // Evolution Node: uses admin token and instance name in URL
      const response = await axios.delete(
        `${baseUrl}/instance/logout/${instanceName}`,
        {
          headers: {
            apikey: adminToken,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data.message || 'Logout failed');
      }
      throw new Error('Network error during logout');
    }
  }

  /**
   * Fetch all instances
   * Evolution GO: GET /instance/all
   * Evolution Node: GET /instance/fetchInstances
   */
  static async fetchInstances(apiUrl, adminToken) {
    if (!apiUrl || !adminToken) {
      return Promise.reject(new Error('Missing Evolution credentials'));
    }

    const baseUrl = apiUrl.replace(/\/$/, '');
    const headers = {
      apikey: adminToken,
      'Content-Type': 'application/json',
    };

    try {
      // Try Evolution GO endpoint first
      const response = await axios.get(`${baseUrl}/instance/all`, { headers });
      // Evolution GO returns { data: [], message: "success" }
      return response.data.data || response.data;
    } catch {
      // Fall back to Evolution Node endpoint
      try {
        const response = await axios.get(`${baseUrl}/instance/fetchInstances`, {
          headers,
        });
        return response.data;
      } catch (error) {
        if (error.response) {
          throw new Error(
            error.response.data.message || 'Failed to fetch instances'
          );
        }
        throw new Error('Network error while fetching instances');
      }
    }
  }

  /**
   * Delete an instance
   * Evolution GO: DELETE /instance/delete/{instanceId} - requires UUID
   * Evolution Node: DELETE /instance/delete/{instanceName}
   */
  static async deleteInstance(
    apiUrl,
    adminToken,
    instanceName,
    instanceId = null
  ) {
    if (!apiUrl || !adminToken || !instanceName) {
      return Promise.reject(new Error('Missing Evolution credentials'));
    }

    const baseUrl = apiUrl.replace(/\/$/, '');
    let deleteId = instanceId || instanceName;

    // For Evolution GO, we need UUID. If not provided, fetch it
    if (!instanceId) {
      try {
        const instances = await this.fetchInstances(apiUrl, adminToken);
        const found = instances.find(
          inst =>
            (inst.instanceName || inst.name) === instanceName ||
            inst.id === instanceName
        );
        if (found && found.id) {
          deleteId = found.id;
        }
      } catch {
        // If fetch fails, try with instanceName anyway
      }
    }

    const url = `${baseUrl}/instance/delete/${deleteId}`;

    try {
      const response = await axios.delete(url, {
        headers: {
          apikey: adminToken,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || 'Failed to delete instance'
        );
      }
      throw new Error('Network error while deleting instance');
    }
  }

  /**
   * Get QR Code for connection
   * Evolution GO: POST /instance/connect then GET /instance/qr (with instanceToken in apikey header)
   * Evolution Node: GET /instance/connect/{instanceName}
   */
  static async getQRCode(
    apiUrl,
    adminToken,
    instanceName,
    instanceToken = null,
    webhookUrl = null
  ) {
    if (!apiUrl || !adminToken || !instanceName) {
      return Promise.reject(new Error('Missing Evolution credentials'));
    }

    const baseUrl = apiUrl.replace(/\/$/, '');

    try {
      // Evolution GO: uses instance token
      if (instanceToken) {
        // Build connect request body with webhook configuration
        const connectBody = {};
        if (webhookUrl) {
          connectBody.webhookUrl = webhookUrl;
          connectBody.subscribe = ['MESSAGE', 'PRESENCE', 'CONNECTION'];
        }

        // First, initiate connection with POST /instance/connect
        try {
          const connectResponse = await axios.post(
            `${baseUrl}/instance/connect`,
            connectBody,
            {
              headers: {
                apikey: instanceToken,
                'Content-Type': 'application/json',
              },
            }
          );

          // Evolution GO may return QR code directly in connect response
          const connectData =
            connectResponse.data?.data || connectResponse.data;
          if (
            connectData?.Qrcode ||
            connectData?.qrcode ||
            connectData?.base64 ||
            connectData?.code
          ) {
            return this.normalizeQrResponse(connectData);
          }
        } catch {
          // Connection might already exist, continue to get QR
        }

        // Then get QR code with GET /instance/qr
        const qrResponse = await axios.get(`${baseUrl}/instance/qr`, {
          headers: {
            apikey: instanceToken,
            'Content-Type': 'application/json',
          },
        });

        return this.normalizeQrResponse(
          qrResponse.data?.data || qrResponse.data
        );
      }

      // Evolution Node: GET /instance/connect/{instanceName}
      const response = await axios.get(
        `${baseUrl}/instance/connect/${instanceName}`,
        {
          headers: {
            apikey: adminToken,
            'Content-Type': 'application/json',
          },
        }
      );
      return this.normalizeQrResponse(response.data);
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data.message || 'Failed to get QR code');
      }
      throw new Error('Network error while getting QR code');
    }
  }

  /**
   * Normalize QR code response to ensure base64 image format
   * Evolution GO returns: { data: { Qrcode: "data:image/png;base64,...", Code: "..." }, message: "success" }
   * Evolution Node returns: { base64: "data:image/png;base64,..." }
   */
  static normalizeQrResponse(data) {
    if (!data) return null;

    // If it's already a string (raw base64 or data URI), return as-is
    if (typeof data === 'string') {
      return data.startsWith('data:') ? data : `data:image/png;base64,${data}`;
    }

    // Try to extract QR code from various possible response structures
    // Evolution GO uses capital Q in "Qrcode"
    const qrCode =
      data.Qrcode ||
      data.base64 ||
      data.qrcode?.base64 ||
      data.qrcode ||
      data.qrCode ||
      data.qr ||
      data.code ||
      data.Code;

    if (!qrCode) return null;

    // Ensure it's a proper data URI for img src
    if (typeof qrCode === 'string') {
      return qrCode.startsWith('data:')
        ? qrCode
        : `data:image/png;base64,${qrCode}`;
    }

    return qrCode;
  }

  /**
   * Get instance info
   * Evolution GO: GET /instance/info/{instanceId}
   */
  static async getInstanceInfo(apiUrl, adminToken, instanceName) {
    if (!apiUrl || !adminToken || !instanceName) {
      return Promise.reject(new Error('Missing Evolution credentials'));
    }
    const url = `${apiUrl.replace(/\/$/, '')}/instance/info/${instanceName}`;

    try {
      const response = await axios.get(url, {
        headers: {
          apikey: adminToken,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || 'Failed to get instance info'
        );
      }
      throw new Error('Network error while getting instance info');
    }
  }

  /**
   * Get instance status
   * Evolution GO: GET /instance/status (with instanceToken in apikey header)
   */
  static async getInstanceStatus(apiUrl, instanceToken) {
    if (!apiUrl || !instanceToken) {
      return Promise.reject(new Error('Missing Evolution credentials'));
    }
    const url = `${apiUrl.replace(/\/$/, '')}/instance/status`;

    try {
      const response = await axios.get(url, {
        headers: {
          apikey: instanceToken,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || 'Failed to get instance status'
        );
      }
      throw new Error('Network error while getting instance status');
    }
  }

  /**
   * Set webhook for instance
   * Evolution GO: Uses different webhook configuration
   * Evolution Node: POST /webhook/set/{instanceName}
   */
  static async setWebhook(apiUrl, adminToken, instanceName, webhook) {
    if (!apiUrl || !adminToken || !instanceName || !webhook) {
      return Promise.reject(new Error('Missing Evolution credentials'));
    }
    const url = `${apiUrl.replace(/\/$/, '')}/webhook/set/${instanceName}`;

    try {
      const response = await axios.post(
        url,
        { webhook },
        {
          headers: {
            apikey: adminToken,
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data.message || 'Failed to set webhook');
      }
      throw new Error('Network error while setting webhook');
    }
  }

  /**
   * Find settings for instance
   * Evolution GO: GET /instance/{instanceId}/advanced-settings (requires UUID and instanceToken)
   * Evolution Node: GET /settings/find/{instanceName}
   */
  static async findSettings(
    apiUrl,
    adminToken,
    instanceName,
    instanceToken = null,
    instanceId = null
  ) {
    if (!apiUrl || !adminToken || !instanceName) {
      return Promise.reject(new Error('Missing Evolution credentials'));
    }

    const baseUrl = apiUrl.replace(/\/$/, '');

    try {
      // Try Evolution GO endpoint first (requires instance token AND instance UUID)
      if (instanceToken) {
        let uuid = instanceId;
        // If no UUID provided, try to fetch it
        if (!uuid) {
          try {
            const instances = await this.fetchInstances(apiUrl, adminToken);
            const found = instances.find(
              inst =>
                (inst.instanceName || inst.name) === instanceName ||
                inst.id === instanceName
            );
            if (found && found.id) {
              uuid = found.id;
            }
          } catch {
            // Fall back to Evolution Node if fetch fails
          }
        }

        if (uuid) {
          const response = await axios.get(
            `${baseUrl}/instance/${uuid}/advanced-settings`,
            {
              headers: {
                apikey: instanceToken,
                'Content-Type': 'application/json',
              },
            }
          );
          return response.data;
        }
      }

      // Fall back to Evolution Node endpoint
      const response = await axios.get(
        `${baseUrl}/settings/find/${instanceName}`,
        {
          headers: {
            apikey: adminToken,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || 'Failed to fetch settings'
        );
      }
      throw new Error('Network error while fetching settings');
    }
  }

  /**
   * Set settings for instance
   * Evolution GO: PUT /instance/{instanceId}/advanced-settings (requires UUID and instanceToken)
   * Evolution Node: POST /settings/set/{instanceName}
   */
  static async setSettings(
    apiUrl,
    adminToken,
    instanceName,
    settings,
    instanceToken = null,
    instanceId = null
  ) {
    if (!apiUrl || !adminToken || !instanceName) {
      return Promise.reject(new Error('Missing Evolution credentials'));
    }

    const baseUrl = apiUrl.replace(/\/$/, '');

    try {
      // Try Evolution GO endpoint first (requires instance token AND instance UUID)
      if (instanceToken) {
        let uuid = instanceId;
        // If no UUID provided, try to fetch it
        if (!uuid) {
          try {
            const instances = await this.fetchInstances(apiUrl, adminToken);
            const found = instances.find(
              inst =>
                (inst.instanceName || inst.name) === instanceName ||
                inst.id === instanceName
            );
            if (found && found.id) {
              uuid = found.id;
            }
          } catch {
            // Fall back to Evolution Node if fetch fails
          }
        }

        if (uuid) {
          const response = await axios.put(
            `${baseUrl}/instance/${uuid}/advanced-settings`,
            settings,
            {
              headers: {
                apikey: instanceToken,
                'Content-Type': 'application/json',
              },
            }
          );
          return response.data;
        }
      }

      // Fall back to Evolution Node endpoint
      const response = await axios.post(
        `${baseUrl}/settings/set/${instanceName}`,
        settings,
        {
          headers: {
            apikey: adminToken,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || 'Failed to update settings'
        );
      }
      throw new Error('Network error while updating settings');
    }
  }

  /**
   * Create a new instance
   * Evolution GO: POST /instance/create
   */
  static async createInstance(apiUrl, adminToken, instanceData) {
    if (!apiUrl || !adminToken || !instanceData) {
      return Promise.reject(new Error('Missing Evolution credentials'));
    }
    const url = `${apiUrl.replace(/\/$/, '')}/instance/create`;

    try {
      const response = await axios.post(url, instanceData, {
        headers: {
          apikey: adminToken,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || 'Failed to create instance'
        );
      }
      throw new Error('Network error while creating instance');
    }
  }

  /**
   * Connect to an instance (initiate WhatsApp connection)
   * Evolution GO: POST /instance/connect (with instanceToken)
   * @param {string} apiUrl - Evolution API URL
   * @param {string} instanceToken - Instance token for authentication
   * @param {Object} webhookConfig - Optional webhook configuration
   * @param {string} webhookConfig.webhookUrl - Webhook URL to receive events
   * @param {string[]} webhookConfig.subscribe - Array of events to subscribe to
   */
  static async connectInstance(apiUrl, instanceToken, webhookConfig = null) {
    if (!apiUrl || !instanceToken) {
      return Promise.reject(new Error('Missing Evolution credentials'));
    }
    const url = `${apiUrl.replace(/\/$/, '')}/instance/connect`;

    // Build request body with optional webhook configuration
    const body = {};
    if (webhookConfig) {
      if (webhookConfig.webhookUrl) {
        body.webhookUrl = webhookConfig.webhookUrl;
      }
      if (webhookConfig.subscribe && webhookConfig.subscribe.length > 0) {
        body.subscribe = webhookConfig.subscribe;
      }
    }

    try {
      const response = await axios.post(url, body, {
        headers: {
          apikey: instanceToken,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || 'Failed to connect instance'
        );
      }
      throw new Error('Network error while connecting instance');
    }
  }

  /**
   * Configure webhook for Evolution GO instance
   * This updates the webhook URL and subscribed events
   * Evolution GO: POST /instance/connect (with instanceToken and webhook config)
   * @param {string} apiUrl - Evolution API URL
   * @param {string} instanceToken - Instance token for authentication
   * @param {string} webhookUrl - Webhook URL to receive events
   * @param {string[]} events - Array of events to subscribe to (default: MESSAGE, PRESENCE, CONNECTION)
   */
  static async configureWebhook(
    apiUrl,
    instanceToken,
    webhookUrl,
    events = null
  ) {
    if (!apiUrl || !instanceToken || !webhookUrl) {
      return Promise.reject(
        new Error('Missing Evolution credentials or webhook URL')
      );
    }

    const defaultEvents = [
      'MESSAGE',
      'SEND_MESSAGE',
      'PRESENCE',
      'CONNECTION',
      'CONTACT',
    ];

    return this.connectInstance(apiUrl, instanceToken, {
      webhookUrl,
      subscribe: events || defaultEvents,
    });
  }

  /**
   * Configure webhook with full options for Evolution GO instance
   * This updates the webhook URL, subscribed events, and messaging options
   * Evolution GO: POST /instance/connect (with instanceToken and full config)
   * @param {string} apiUrl - Evolution API URL
   * @param {string} instanceToken - Instance token for authentication
   * @param {string} webhookUrl - Webhook URL to receive events
   * @param {string[]} events - Array of events to subscribe to
   * @param {string} rabbitmqEnable - RabbitMQ option ('default', 'enabled', 'disabled')
   * @param {string} websocketEnable - WebSocket option ('default', 'enabled', 'disabled')
   * @param {string} natsEnable - NATS option ('default', 'enabled', 'disabled')
   */
  static async configureWebhookFull(
    apiUrl,
    instanceToken,
    webhookUrl,
    events,
    rabbitmqEnable = 'default',
    websocketEnable = 'default',
    natsEnable = 'default'
  ) {
    if (!apiUrl || !instanceToken || !webhookUrl) {
      return Promise.reject(
        new Error('Missing Evolution credentials or webhook URL')
      );
    }

    if (!events || events.length === 0) {
      return Promise.reject(new Error('At least one event must be selected'));
    }

    const url = `${apiUrl.replace(/\/$/, '')}/instance/connect`;

    // Map messaging options to Evolution GO format
    const mapOption = option => {
      switch (option) {
        case 'enabled':
          return 'true';
        case 'disabled':
          return 'false';
        default:
          return ''; // 'default' means use server default (empty string)
      }
    };

    const body = {
      webhookUrl,
      subscribe: events,
      rabbitmqEnable: mapOption(rabbitmqEnable),
      websocketEnable: mapOption(websocketEnable),
      natsEnable: mapOption(natsEnable),
    };

    try {
      const response = await axios.post(url, body, {
        headers: {
          apikey: instanceToken,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || 'Failed to configure webhook'
        );
      }
      throw new Error('Network error while configuring webhook');
    }
  }

  /**
   * Reconnect an instance
   * Evolution GO: POST /instance/reconnect (with instanceToken)
   */
  static async reconnectInstance(apiUrl, instanceToken) {
    if (!apiUrl || !instanceToken) {
      return Promise.reject(new Error('Missing Evolution credentials'));
    }
    const url = `${apiUrl.replace(/\/$/, '')}/instance/reconnect`;

    try {
      const response = await axios.post(
        url,
        {},
        {
          headers: {
            apikey: instanceToken,
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || 'Failed to reconnect instance'
        );
      }
      throw new Error('Network error while reconnecting instance');
    }
  }

  /**
   * Disconnect an instance
   * Evolution GO: POST /instance/disconnect (with instanceToken)
   */
  static async disconnectInstance(apiUrl, instanceToken) {
    if (!apiUrl || !instanceToken) {
      return Promise.reject(new Error('Missing Evolution credentials'));
    }
    const url = `${apiUrl.replace(/\/$/, '')}/instance/disconnect`;

    try {
      const response = await axios.post(
        url,
        {},
        {
          headers: {
            apikey: instanceToken,
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || 'Failed to disconnect instance'
        );
      }
      throw new Error('Network error while disconnecting instance');
    }
  }
}

export default EvolutionAPI;
